import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import copy from 'copy-to-clipboard';
import { configsSelector } from '../../store/reducers/servicesConfigs';
import { loadingPredictionsSelector } from '../../store/reducers/predictions';
import {
  makePrediction,
  makePredictions,
} from '../../store/actions/predictions';
import { getImageDimensions } from '../../utils';
import { flagsSelector } from '../../store/reducers/flags';

const useStyles = makeStyles((theme) => ({
  select: {
    margin: theme.spacing(2),
    minWidth: 300,
  },
  largeSpace: {
    marginLeft: theme.spacing(13),
  },
  numberInput: {
    margin: theme.spacing(1),
    width: 100,
  },
  savedHeight: {
    minHeight: 100,
  },
  textArea: {
    minWidth: 440,
    marginBottom: theme.spacing(3),
  },
  button: {
    backgroundColor: '#2ca0f7',
    color: 'white',
    textTransform: 'none',
    fontFamily: 'Exo 2',
    marginLeft: theme.spacing(1),
  },
  message: {
    margin: 'auto',
    color: 'red',
  },
  space: {
    marginRight: theme.spacing(1),
  },
  ratio: {
    width: '75px',
    margin: '8px',
  },
}));

const croppedAreas = [
  '325x260 (ratio:0.8)',
  '400x320 (ratio:0.8)',
  '420x390 (ration:0.93)',
  'Custom',
];

const customCroppedAreaValue = croppedAreas.findIndex(
  (croppedArea) => croppedArea === 'Custom'
);

const initialRealCrop = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
};

const initialPercentCrop = { unit: '%', ...initialRealCrop };

export default () => {
  const classes = useStyles();
  const [percentCrop, setPercentCrop] = useState(initialPercentCrop);
  const [realCrop, setRealCrop] = useState(initialRealCrop);
  const [currentConfig, setCurrentConfig] = useState(0);
  const [currentCroppedArea, setCroppedArea] = useState(0);
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const [composeYMLText, setComposeYMLText] = useState('');
  const [configYMLText, setConfigYMLText] = useState('');
  const image = useSelector(
    (state) => state.predictions.predictions[currentConfig]?.response?.image
  );
  const configs = useSelector(configsSelector);
  const loading = useSelector(loadingPredictionsSelector);
  const flags = useSelector(flagsSelector);

  const config = configs[currentConfig];
  const configOptions = configs.map(({ host, port }) => `${host}:${port}`);
  const dispatch = useDispatch();

  const onConfigChange = ({ target }) => {
    setCurrentConfig(target.value);
  };

  const onCroppedAreaChange = ({ target }) => {
    setCroppedArea(target.value);
  };

  const onCapture = () => {
    dispatch(makePrediction(config, currentConfig, { _predict: false }));
  };

  const handleCoordinateChange = ({ target }) => {
    const getDivider = (prop) =>
      ['x', 'width'].includes(prop) ? imageWidth : imageHeight;
    const { name, value } = target;
    setRealCrop({
      ...realCrop,
      [name]: +value,
    });
    setPercentCrop({
      ...percentCrop,
      [name]: Math.ceil((value / getDivider(name)) * 100),
    });
  };

  const onCropChange = (_, newPercentCrop) => {
    const { x, y, width, height } = newPercentCrop;
    let newWidth = Math.floor((width * imageWidth) / 100);
    newWidth =
      Math.abs(newWidth - realCrop.width) > 1 ? newWidth : realCrop.width;
    let newHeight = Math.floor((height * imageHeight) / 100);
    newHeight =
      Math.abs(newHeight - realCrop.height) > 1 ? newHeight : realCrop.height;
    const newRealCrop = {
      x: Math.ceil((x * imageWidth) / 100),
      y: Math.ceil((y * imageHeight) / 100),
      width: newWidth,
      height: newHeight,
    };
    if (
      ['width', 'height'].some((prop) => newRealCrop[prop] !== realCrop[prop])
    ) {
      setCroppedArea(customCroppedAreaValue);
    }
    setPercentCrop(newPercentCrop);
    setRealCrop(newRealCrop);
  };

  const onComposeYMLCopy = () => {
    copy(composeYMLText);
  };

  const onConfigYMLCopy = () => {
    copy(configYMLText);
  };

  const coordinateProps = {
    type: 'number',
    InputProps: { inputProps: { min: 0 } },
    onChange: handleCoordinateChange,
    variant: 'outlined',
    className: classes.numberInput,
  };

  useEffect(() => {
    dispatch(makePredictions({ _predict: false }, flags));
  }, []);

  useEffect(() => {
    (async () => {
      const { width, height } = await getImageDimensions(
        `data:image/jpeg;base64,${image}`
      );
      setImageWidth(width);
      setImageHeight(height);
    })();
  }, [image]);

  useEffect(() => {
    if (
      currentCroppedArea !== customCroppedAreaValue &&
      imageWidth &&
      imageHeight
    ) {
      const area = croppedAreas[currentCroppedArea];
      const width = +area.substring(0, 3);
      const height = +area.substring(4, 7);
      setRealCrop({
        ...realCrop,
        width,
        height,
      });
      setPercentCrop({
        ...percentCrop,
        width: (width / imageWidth) * 100,
        height: (height / imageHeight) * 100,
      });
    }
  }, [currentCroppedArea, imageWidth, imageHeight]);

  useEffect(() => {
    const { x, y, width, height } = realCrop;
    setComposeYMLText(`      - EDGIFY_VIDEOSTREAM_CROP_ENABLE=true
      - EDGIFY_VIDEOSTREAM_CROP_X1=${x}
      - EDGIFY_VIDEOSTREAM_CROP_Y1=${y}
      - EDGIFY_VIDEOSTREAM_CROP_X2=${x + width}
      - EDGIFY_VIDEOSTREAM_CROP_Y2=${y + height}`);
    setConfigYMLText(`      crop_enable: false
      crop_x1: ${x}
      crop_y1: ${y}
      crop_x2: ${x + width}
      crop_y2: ${y + height}`);
  }, [realCrop]);

  useEffect(() => {
    onCapture();
    const onKeyDown = (event) => {
      if (event.keyCode === 16 && !loading) {
        onCapture();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [config]);

  return {
    ...config,
    configOptions,
    currentConfig,
    image,
    imageDimension: `${imageWidth}x${imageHeight}`,
    croppedAreas,
    currentCroppedArea,
    isCustomArea: currentCroppedArea === customCroppedAreaValue,
    percentCrop,
    realCrop,
    coordinateProps,
    composeYMLText,
    configYMLText,
    loading,
    classes,
    onConfigChange,
    onCroppedAreaChange,
    onCapture,
    onCropChange,
    onComposeYMLCopy,
    onConfigYMLCopy,
  };
};
